<template>
  <NuxtLayout
    :name="layout"
    :theme="effectiveTheme"
  >
    <BasicBtErrorBoundary>
      <ClientOnly>
        <LazyBasicSessionReplay v-if="userGroup == 'internal' || userGroup == 'expzentrale'" />
      </ClientOnly>
    </BasicBtErrorBoundary>
    <Transition mode="in-out">
      <div
        v-if="showInfo"
        class="bubble flex gap-y-4 p-4 text-primary"
      >
        <div class="bubble-container container">
          <BasicIcon
            :name="'location-dot-solid'"
            class="inline-block w-4 text-primary"
          />
          <span class="pl-1 w-full">
            Sie wurden einem lokalem Fachmarkt in der Nähe zugewiesen!
            <u>
              <NuxtLink
                :external="true"
                href="/Footer/Website/Datenschutzerklaerung#:~:text=16.%20Geo%2DLokalisierung%20f%C3%BCr%20den%20Fachmarkt%2DFinder"
              >
                Mehr erfahren
              </NuxtLink>
            </u>
          </span>
        </div>
      </div>
    </Transition>
    <NuxtPage />
    <NuxtLoadingIndicator
      :color="'#ff5e19'"
      :height="device.isMobile ? 4 : 10"
      :duration="4"
    />
    <div id="modals">
      <PopUpsShared />
    </div>
    <div
      v-show="leaflet.value?.showing"
      id="rich-content-tooltip"
      :style="{ top: leaflet.value?.yPos + 'px', left: leaflet.value?.xPos + 'px' }"
      @mouseenter="keepAlive"
      @mouseleave="startDeath"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="testresults"
        v-html="leaflet.value?.content"
      />
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useBroadcastChannel, useWindowSize } from '@vueuse/core';
import { useRuntimeConfig } from 'nuxt/app';
import { provide } from 'vue';
import rImage from '~/assets/img/rImage.png';
import rAud from '~/assets/sounds/rAud.mp3';
import { initializeStore, initializeStoreInApp } from '~/composables/appInitialization';
import { getExpUserProfile } from '~/composables/ExpApi/expUser';
import { getWishlist } from '~/composables/expWishList';
import { ProvideNames, StateNames, TimeUnit, withCdn } from '#shared/statics';
import { fetchNavigationTree, getExpNavigationTree } from './composables/ExpApi/expCache';
import { keepAlive, startDeath } from './composables/leafletUtils';
import type { NeaWishlist } from '@expert/types';

const logger = useLogger('app.vue');

logger.start('Starting app.vue initialization');

const rConfig = useRuntimeConfig();
const userGroup = useUserGroup()?.value;
const device = useDevice();

// #region Initiate App States <App Detection>
try {
  initializeAppContext();
  logger.success('Initialized app context');
} catch (error) {
  logger.error('could not set context value', error);
}
// #endregion
const isAppUser = useAppConfig()?.isAppUser;

const layout = computed(() => {
  if (isAppUser) {
    return 'app';
  }
  return undefined;
});

useSchemaOrg([
  {
    '@id': 'https://www.expert.de/#organization',
    '@type': 'Organization',
    name: 'expert',
    url: 'https://www.expert.de/',
    logo: {
      '@id': 'https://www.expert.de/#logo',
      '@type': 'ImageObject',
      url: withCdn(ProvideNames.DEFAULT_CENTRAL_STORE.storeLogoImg),
    },
  },
  {
    '@id': 'https://www.expert.de/#website',
    '@type': 'WebSite',
    name: 'Elektromarkt in Ihrer Nähe mit Online-Shop – expert',
    url: 'https://www.expert.de/',
    publisher: { '@id': 'https://www.expert.de/#organization' },
  },
]);
const contrastMode = useContrastMode();

const seasonalThemeEnabled = useFeatureState('cfg-seasonal-theme');
const useSeasonalTheme = useFeatureValue('cfg-seasonal-theme');

const forcedTheme = ref<AppTheme | null>(null);
const seasonalTheme = computed(() => {
  if (seasonalThemeEnabled.value === true) {
    const featVal = useSeasonalTheme.value;
    if (featVal != null && typeof featVal === 'object' && 'name' in featVal && isAppTheme(featVal?.name)) {
      return featVal.name;
    }
  }
  return null;
});

provide('theme', seasonalTheme);

const currentStore = useCurrentStore();

watch(
  currentStore,
  (newStore) => {
    if ('1906674' === newStore?.companyGroupId) {
      forcedTheme.value = 'blue-schimmel-cheese';
    }
  },
  { immediate: true },
);

const route = useRoute();

const effectiveTheme = computed<AppTheme>(() => {
  return (route.meta.disableTheme ? null : (forcedTheme.value ?? seasonalTheme.value)) ?? 'default';
});

const viewportContent = computed(() =>
  contrastMode.value
    ? 'width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=5.0'
    : 'width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no',
);

useHead({
  htmlAttrs: {
    'data-contrast': () => (contrastMode.value ? 'high' : 'normal'),
    'data-theme': effectiveTheme,
  },
  meta: [
    {
      id: 'viewport',
      name: 'viewport',
      content: viewportContent,
    },
  ],
});

const currentUserState = useCurrentUserState();

// #region Initiate store cookie and state
try {
  await callOnce(async () => {
    if (rConfig.public.isPWABuild || useRoute().path.startsWith('/app')) {
      await initializeStoreInApp();
    } else {
      await initializeStore();
    }
  });
  logger.success('Initialized current store');
} catch (error) {
  logger.error('could not set store state', error);
}
// #endregion

// #region Initiate current user
const { refresh: keycloakRefresh, loggedIn: keycloakLoggedIn } = useOidcAuth();
try {
  await callOnce(async () => {
    useCurrentUserState().value = await getExpUserProfile();
  });

  const { isSupported: isBroadcastSupported, data: userSessionChannel } = useBroadcastChannel({
    name: 'user-profile-channel',
  });

  if (isBroadcastSupported) {
    watch(userSessionChannel, async () => {
      useState('appUserProfile').value = await $fetch('/api/user/getProfileData');
    });
  }

  if (useState<AppUserSession>(StateNames.APP_USER_SESSION).value?.features.payback.enabled) {
    $fetch('/api/payback/session/access-token', {
      headers: {
        cookie: useRequestHeader('cookie'),
      },
      retryStatusCodes: [401, 408, 409, 425, 429, 500, 502, 503, 504],
      retry: 5,
    });
  }
  logger.success('Initialized current user state');
} catch (error) {
  logger.error('could not set user session', error);
}
// #endregion

// #region Initiate shoppingCart and reservationCart
try {
  await callOnce(async () => {
    await initializeCartStates();
    logger.success('Initialized shopping carts');
  });
} catch (error) {
  logger.error('could not set shopping carts', error);
}
// #endregion

// #region Initiate promotion index
try {
  const _promotionIndex = await useFetch<PromotionIndex>('/api/promotions/getIndex', {
    key: `promotion/index`,
    params: computed(() => {
      return {
        storeId: currentStore.value?.id,
      };
    }),
  });
} catch (error) {
  logger.error(`Couldn't build promotion index.`, error);
}
// #endregion

// #region Initiate navigation trees
await callOnce(async () => {
  const navigationTrees: Array<string> = ['MAIN', 'SUPER', 'FOOTER', 'FOOTER_HARDLINKS', 'FOOTER_SOCIAL'];

  for (const navigationKey of navigationTrees) {
    try {
      getExpNavigationTree(navigationKey).data.value = await fetchNavigationTree(navigationKey);
      logger.success('Initialized navigation tree for key: ', navigationKey);
    } catch (error) {
      logger.error(`Couldn't initialize navigation tree for key: '${navigationKey}'`, error);
    }
  }
});
// #endregion

// #region Set global watchers
const { width } = useWindowSize();

watch(
  width,
  (newWidth) => {
    if (newWidth === Infinity) {
      return;
    }
    if (newWidth < 1200 && device.isDesktop) {
      device.isMobile = true;
      device.isDesktop = false;
    } else if (newWidth > 1200 && useDevice().isMobile) {
      device.isMobile = false;
      device.isDesktop = true;
    }
  },
  { immediate: true },
);

const { isSupported, data, post } = useBroadcastChannel({
  name: 'fmarkt-channel',
});

const broadcastEnabled = computed(() => {
  return isSupported.value && !device.isCrawler;
});

const router = useRouter();
const storeCookie = useStoreCookie();

watch(data, () => {
  if (device.isCrawler) {
    return;
  }
  if (currentStore.value?.id != data?.value) {
    const lcPath = route.fullPath.toLowerCase();
    if (route.query || lcPath.includes('branch_id')) {
      router.push({
        path: route.path,
        query: {},
      });
    }
    nextTick(async () => {
      logger.error('got message for different store!! will switch');
      if (storeCookie.value) {
        await changeStoreById(storeCookie.value, currentStore.value?.id, 'broadcast-message');
      }
    });
  }
});

const { data: userContextChannelData } = useBroadcastChannel({
  name: 'user-context-channel',
});

watch(userContextChannelData, () => {
  if (userContextChannelData.value != undefined && userContextChannelData.value !== currentUserState.value.userId) {
    nextTick(async () => {
      logger.info('Got new user id from channel. Start user refetching.');
      await getExpUserProfile();
    });
  }
});
logger.success('Initialized global watchers');
// #endregion

useNuxtApp().vueApp.config.warnHandler = (msg, _instance, _trace) => {
  // `trace` is the component hierarchy trace
  if (!msg?.includes('Hydration')) {
    logger.warn('[vue-warning] ', msg);
  }
};

const showInfo = ref(false);

// #region Store Change Handling>

const currentSessionIdentifier = useCurrentSessionIdentifier();
const btInternalTest = useFeatureState('bt-internal-test');
const abConfigCookie = useABConfigCookie();

onNuxtReady(async () => {
  if (broadcastEnabled.value == true && isAppUser != true) {
    post(storeCookie.value);
  }
  if (import.meta.client && useAppConfig()?.isAppUser != true) {
    if (currentStore.value?.id != useStoreCookie()?.value && useStoreCookie()?.value) {
      logger.debug('state and cookie do not match');
      const storeCookie = useStoreCookie()?.value;
      if (storeCookie) {
        await changeStoreById(storeCookie, useCurrentStore()?.value?.id, 'state-cookie-mismatch');
      }
    }
    watchEffect(async () => {
      if (currentSessionIdentifier.value) {
        if (btInternalTest.value) {
          // startTracking()
        }

        if (abConfigCookie.value != undefined && abConfigCookie.value?.context?.consented == undefined) {
          setABContextVariable('consented', true);
          // resW();
        }
      }
    });
  }
});
// #endregion Store Change Handling>

if (!device.isCrawler) {
  let heartbeatTimeout: NodeJS.Timeout;
  const HEARTBEAT_BASE = 10 * TimeUnit.MINUTE * 1000;
  const HEARTBEAT_JITTER = 4 * TimeUnit.MINUTE * 1000;

  const nextHeartbeatDelay = () =>
    // base +/- (HEARTBEAT_JITTER / 2) -> e.g. 10min +/- 2min
    HEARTBEAT_BASE + (Math.random() - 0.5) * HEARTBEAT_JITTER;

  const scheduleHeartbeat = () => {
    heartbeatTimeout = setTimeout(() => {
      $fetch('/api/neo/frontend/_api/user/heartbeat', { method: 'POST', body: '{}' });
      if (keycloakLoggedIn.value) {
        keycloakRefresh();
      }
      scheduleHeartbeat();
    }, nextHeartbeatDelay());
  };

  onNuxtReady(() => {
    scheduleHeartbeat();
  });

  onBeforeUnmount(() => {
    if (heartbeatTimeout) {
      clearTimeout(heartbeatTimeout);
    }
  });
}

const ga4State = useGA4State();
const cfgClicktracking = useFeatureState('cfg-clicktracking');
const cfgLinktracking = useFeatureState('cfg-linktracking');

onNuxtReady(() => {
  if (window) {
    window.btcheckABConf = () => {
      logger.warn('ABConfigCookie: ', useABConfigCookie().value);
    };
    try {
      window.dataLayer = window.dataLayer || [];
      function gtag(...args) {
        window.dataLayer.push(args);
      }
      gtag('consent', 'default', {
        ad_personalization: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      });
      gtag('set', 'ads_data_redaction', true);

      const gclidPromise = new Promise((resolve) => {
        gtag('get', rConfig.public.gtm?.gaId, 'gclid', resolve);
      });
      const clientIdPromise = new Promise((resolve) => {
        gtag('get', rConfig.public.gtm?.gaId, 'client_id', resolve);
      });
      const sessionIdPromise = new Promise((resolve) => {
        gtag('get', rConfig.public.gtm?.gaId, 'session_id', resolve);
      });
      const sessionNumberPromise = new Promise((resolve) => {
        gtag('get', rConfig.public.gtm?.gaId, 'session_number', resolve);
      });

      gclidPromise.then((gclid) => {
        setABContextVariable('ga4_gclid', gclid);
        ga4State.value.gclid = gclid as string;
      });
      clientIdPromise.then((client_id) => {
        setABContextVariable('ga4_client_id', client_id);
        ga4State.value.client_id = client_id as string;
        console.log('got ga4 client id', client_id);
      });
      sessionIdPromise.then((session_id) => {
        setABContextVariable('ga4_session_id', session_id);
        ga4State.value.session_id = session_id as string;
      });
      sessionNumberPromise.then((session_number) => {
        setABContextVariable('ga4_session_number', session_number);
        ga4State.value.session_number = session_number as number;
      });
    } catch (error) {
      logger.warn('could not set default consent', error);
    }
  }
  // #region <Click Tracking>
  logger.debug('nuxt ready - adding click event listener');
  document.addEventListener('click', (ev) => {
    const targetData = {
      group:
        ev.target?.getAttribute('data-tgroup')
        || ev.target?.getAttribute('aria-label')
        || checkTenParents(ev.target, 'aria-label'),
      name:
        ev.target?.getAttribute('data-tname')
        || (ev.target?.getAttribute('type') == 'button'
          ? ev.target?.textContent?.trim()
          : ev.target.getAttribute('class')),
      detail: ev.target?.getAttribute('data-tdetail') || checkTenParents(ev.target, 'data-tdetail'),
      href: ev.target?.getAttribute('href') || checkTenParents(ev.target, 'href'),
      type: ev.target?.getAttribute('type') || checkTenParents(ev.target, 'type'),
    };
    if (cfgClicktracking.value == true || (cfgLinktracking.value == true && (targetData?.href || targetData?.type))) {
      const eve = {
        eventType: 'USER_INPUT',
        tickId: crypto.randomUUID(),
        sessionId: currentSessionIdentifier.value,
        json: {
          clientX: ev.clientX,
          clientY: ev.clientY,
          pointerType: ev.pointerType,
          target: ev.target?.localName,
          ...targetData,
          type: ev.type,
        },
      };
      useGeneralTrick(eve);
    }
  });
  // #endregion </Click Tracking>
});

if (rConfig.public.speedkit) {
  useHead({
    script: [
      {
        src: 'https://expert.app.baqend.com/v1/speedkit/install.js',
        async: true,
        crossorigin: 'anonymous',
      },
    ],
  });
}

const requestUrl = useRequestURL();
const { data: canonical } = await useFetch<string>(
  `${rConfig.public.apiUrl}/api/nea/canonical-service/canonicalProvider?url=${encodeURIComponent(rConfig.public.siteUrl + requestUrl.pathname + requestUrl.search)}${route.params.webcode != null ? `&webcode=${route.params.webcode}` : ``}`,
);

useHead({
  link: canonical.value != null && canonical.value.trim() != '' ? [{ rel: 'canonical', href: canonical.value }] : [],
});

// #region <CookieBot and Google Tag Manager Init>
const gtm = useGtm();

onNuxtReady(() => {
  if (
    device?.isLighthouse == true
    || useState('isLighthouse')?.value == true
    || useIsCrawlerState()?.value
    || useAppConfig()?.isAppUser
  ) {
    logger.info('loading nothing');
  } else {
    useHead({
      script: [
        {
          src: 'https://consent.cookiebot.eu/uc.js',
          'data-cbid': '36f7f725-f52e-4678-a71c-fe212fc2c34e',
          id: 'CookieDeclaration',
          type: 'text/javascript',
          // 'data-blockingmode': 'auto'
          'data-blockingmode': computed(() => {
            const lc_path = route.fullPath?.toLowerCase();
            return !lc_path.includes('/wishlist')
              && (!lc_path.includes('/checkout') || lc_path.includes('/confirmation'))
              ? 'auto'
              : 'none';
          }),
        },
      ],
    });

    window.addEventListener('CookiebotOnConsentReady', function () {
      gtm?.enable(true);
      consented();
      // onConsented()
      logger.debug('handling Cookiebot Callback');
      const prefs = {
        REQUIRED: Cookiebot.consent.necessary,
        STATISTICS: Cookiebot.consent.statistics,
        MARKETING: Cookiebot.consent.marketing,
        PRODUCT_RATINGS: Cookiebot.consent.preferences,
      };
      type PrefName = keyof typeof prefs;
      // let currentCookie = {};
      // const cookieStr = document.cookie.split(';').map((cookie) => cookie.trim()).filter((cookie) => cookie != null && cookie.indexOf('UserCookiePermissions') == 0)[0]?.split('=')?.pop();
      // if (cookieStr != null) {
      //   try {
      //     currentCookie = JSON.parse(decodeURIComponent(cookieStr));
      //   } catch {
      //     currentCookie = {};
      //   }
      // }
      // if (Object.keys(prefs).filter((cookieName) => prefs[cookieName] != currentCookie[cookieName]).length > 0) {
      $fetch('/api/user/consent', {
        method: 'POST',
        headers: {
          'bt-use-user-auth': 'true',
          ...(currentUserState.value?.csrfToken ? { 'csrf-token': currentUserState.value?.csrfToken } : {}),
        },
        body: JSON.stringify({
          preference: prefs,
          privacyPreferenceInfo: 'CookiebotRenderPostProcessor',
          IABConsentString: Cookiebot.IABConsentString,
          mobile: device.isMobile,
        }),
      });
      setABContextVariable('consented', true);
      document.cookie = 'UserCookiePermissions=' + encodeURIComponent(JSON.stringify(prefs));

      (Object.keys(prefs) as PrefName[]).forEach((key) => {
        useState<boolean>(`cookie${key}`).value = prefs[key];
      });
      // }
    });
  }
});
// #endregion </CookieBot and Google Tag Manager Init>

const identifier = useExpCookieIdentifier();
const wishlist = useCurrentWishList();
const loggedInState = useCurrentLoggedInState();

onNuxtReady(async () => {
  const ratingsCookie = getCookiePermissions(PermissionMapping.PRODUCT_RATINGS);

  if (device.isLighthouse || device.isCrawler) {
    const cb = document?.getElementById('CookieDeclaration');
    if (cb) {
      cb.remove();
    }
  } else if (!ratingsCookie?.value) {
    const bv = document?.getElementById('bazaarvoicejs');
    if (bv) {
      bv.remove();
    }
  }

  if (!currentUserState.value && !device.isCrawler) {
    const expUser = await getExpUserProfile();
    if (expUser) {
      currentUserState.value = expUser;
    } else {
      clearNuxtState('userProfile');
    }
  }

  watch(
    () => identifier,
    async (val) => {
      if (val) {
        logger.debug('fetching wishList');
        wishlist.value = (await getWishlist()) as NeaWishlist;
      }
    },
    { immediate: true },
  );

  if (loggedInState.value != true) {
    if (currentUserState.value?.csrfToken == undefined) await getExpUserProfile();
    await $fetch('/api/login', {
      headers: {
        ...(currentUserState.value?.csrfToken != null ? { 'csrf-token': currentUserState.value?.csrfToken } : {}),
      },
      credentials: 'include',
    });

    clearNuxtState(['userProfile']);

    await getExpUserProfile();
    await fetchCurrentWishList();
    await initializeCartStates();
  } else if (useCurrentLoggedInState()?.value == true) {
    logger.debug('logged in state == true, checking for jwt');
  } else {
    logger.debug('logged in state == false, and no jwt found, never logged in?');
  }
});

onNuxtReady(() => {
  const isAllowedSemaphore: Ref<boolean> = ref(true);
  const pattern = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];
  let current = 0;

  const keyHandler = (event: KeyboardEvent) => {
    current = event.keyCode == pattern[current] ? current + 1 : 0;
    if (pattern.length === current) {
      current = 0;
      if (isAllowedSemaphore.value) {
        isAllowedSemaphore.value = false;
        const body = document.getElementsByTagName('body')[0];
        const img = document.createElement('img');
        const aud = document.createElement('audio');
        img.src = rImage;
        img.id = 'rAnimate';
        aud.src = rAud;

        if (body) {
          body.appendChild(img);
          body.appendChild(aud);
        }

        setTimeout(() => {
          aud.play();
        }, 2000);

        setTimeout(() => {
          isAllowedSemaphore.value = true;
          if (body) {
            body.removeChild(img);
            body.removeChild(aud);
          }
        }, 3000);
      }
    }
  };

  document.addEventListener('keydown', keyHandler, false);
});

provide(ProvideNames.CURRENT_STORE_KEY, {
  currentStore: currentStore,
  updateFunction: changeStoreById,
});

provide(ProvideNames.BASE_DIR, '/shop/unsere-produkte/');
provide('ImagePlaceHolder', '/17/89/06/2d7e8a948fb264651203723a2101d1283b/defaultImage_100x100_90-0.jpeg');

useGeneralTrick({
  eventType: 'VIEW_PAGE',
  sessionId: useCurrentSessionIdentifier().value,
  json: {
    deviceType: useDevice().isDesktop
      ? 'desktop'
      : useDevice().isMobile
        ? 'smartphone'
        : useDevice().isTablet
          ? 'tablet'
          : 'unknown',
  },
});

const leaflet = computed(() => {
  return getLeafletConf();
});
</script>

<style lang="postcss">
body {
  @apply bg-page-background;
}
.page-enter-active,
.page-leave-active {
  transition: all 0.4s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  filter: blur(1rem);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.bubble {
  text-align: center;
  background-color: aliceblue;
}

.bubble-container {
  max-width: 1200px;
  max-height: 120px;
  margin: auto;
}

#rAnimate {
  position: fixed;
  bottom: 300px;
  left: 100%;
  width: 300px;
  animation-name: anim;
  animation-duration: 3s;
  animation-timing-function: linear;
}

@keyframes anim {
  0% {
    left: 100%;
    bottom: 0;
    transform: rotateZ(0deg);
  }

  45% {
    left: 55%;
    bottom: 0;
    transform: rotateZ(0deg);
  }

  50% {
    left: 50%;
    bottom: 100px;
    transform: rotateZ(180deg);
  }

  55% {
    left: 45%;
    bottom: 0;
    transform: rotateZ(360deg);
  }

  100% {
    left: calc(0% - 300px);
    bottom: 0;
    transform: rotateZ(360deg);
  }
}

#rich-content-tooltip {
  padding: 15px;
  position: fixed;
  background: rgb(var(--page-background));
  box-shadow: 0px 3px 5px 4px #e5e7eb;
  z-index: 1000;
}

#rich-content-tooltip:before {
  width: 0;
  height: 0;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-bottom: 15px solid rgb(var(--page-background));
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  transform: translateY(-100%);
}

#rich-content-tooltip .testresults {
  max-height: 600px;
  max-width: 500px;
  overflow: auto;
  & a {
    @apply underline;
  }
}
</style>

<style lang="postcss" class="color-mode-css">
[data-theme='blackweek'] .header {
  --paragraph-light: white;
  --color-secondary: dark-grey;
}

[data-theme='blackweek'] .local-store,
[data-theme='blackweek'] .useraccount {
  background-color: #00040d;
}

[data-theme='blackweek'] .useraccount,
[data-theme='blackweek'] .flyout,
[data-theme='blackweek'] .local-store,
[data-theme='blackweek'] .navigation-mobile {
  background-color: white;
  color: black;
}

[data-theme='blackweek'] header {
  background-color: #00040d;
  color: white;
}
[data-theme='blackweek'] .campaign-view .info-title, [data-theme='blackweek'] .campaign-view .back-button {
  @apply text-white;
}
[data-theme='blackweek'] .sticky-header {
  background-color: black;
  color: black;
}

[data-theme='blackweek'] .l-checkout .sticky-header {
  @apply text-white;
}

[data-theme='blackweek'] .UserAccountButton,
[data-theme='blackweek'] .LocalStoreButton {
  color: white;
}

[data-theme='blackweek'] .super-navbar .super-navbar-nav > .nav-item {
  background-color: black;
  border-color: white;
  color: white;
}
[data-theme='blackweek'] {
  & .customer-number-editor {
    @apply bg-black;
    & .text-paragraph {
      @apply text-white;
    }
  }
  & .modal-header {
    @apply bg-black text-white;
  }
}

[data-theme='blue-schimmel-cheese'] .megamenu-container div,
[data-theme='blue-schimmel-cheese'] .storefinder,
[data-theme='blackweek'] .megamenu-container div,
[data-theme='blackweek'] .storefinder {
  /* background-color: #00040D; */
  color: black;
}

[data-theme='blue-schimmel-cheese'] .header {
  --paragraph-light: white;
  --paragraph: rgb(255, 219, 219);
  --color-secondary: dark-grey;
}

[data-theme='blue-schimmel-cheese'] header,
[data-theme='blue-schimmel-cheese'] .sticky-header {
  color: white;
  background-color: #004899;
}

[data-theme='blue-schimmel-cheese'] .useraccount,
[data-theme='blue-schimmel-cheese'] .flyout,
[data-theme='blue-schimmel-cheese'] .local-store {
  background-color: white;
  color: black;
}

.bg-green {
  background-color: #0a4a2e;
  color: white;
  --color-secondary: dark-grey;
}

[data-theme='christmas'] .header {
  --paragraph-light: white;
  --color-secondary: dark-grey;
}

[data-theme='christmas'] .local-store,
[data-theme='christmas'] .useraccount {
  background-color: #0a4a2e;
}

[data-theme='christmas'] .useraccount,
[data-theme='christmas'] .flyout,
[data-theme='christmas'] .local-store {
  background-color: white;
  color: black;
}

[data-theme='christmas'] header {
  background-color: #0a4a2e;
  color: white;
}

[data-theme='christmas'] .sticky-header {
  background-color: #0a4a2e;
  color: black;
}

[data-theme='christmas'] .UserAccountButton,
[data-theme='christmas'] .LocalStoreButton {
  color: white;
}

[data-theme='christmas'] .super-navbar .super-navbar-nav > .nav-item {
  background-color: #0a4a2e;
  border-color: white;
  color: white;
}
[data-theme='christmas'] {
  & .customer-number-editor {
    @apply bg-black;
    & .text-paragraph {
      @apply text-white;
    }
  }
}
</style>

<style lang="postcss" class="theme-css">
[data-theme='blackweek'] header {
  @apply bg-black;
}

[data-theme='blackweek'] header .store-logo-container {
  @apply bg-black;
}

[data-theme='blackweek'] .main-app {
  @apply bg-black text-white;
  @apply min-h-screen;
}

[data-theme='blackweek'] .main-app .topic-package {
  @apply text-white bg-[#393939] border-[#555555] rounded-b;
}

[data-theme='blackweek'] .main-app .topic-preview {
  @apply text-white bg-[#393939] rounded-b;
}

[data-theme='blackweek'] .main-app .topic-preview .entry {
  @apply text-white border-[#555555];
}

[data-theme='blackweek'] .main-app .topic-preview .entry:last-child {
  @apply text-white border-[#555555];
}

[data-theme='blackweek'] .information-teaser {
  @apply text-white bg-[#393939];
}
</style>
